/**
 * Master Inventory Page
 * Main container with tabs for List, Transactions, and Configurations
 */

import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Typography, Tabs, Tab, Paper, Alert, Snackbar, Card, Button,
  useTheme, alpha, Fade
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  SwapHoriz as TransferIcon
} from '@mui/icons-material';
import { usePermissions } from '../../utils/permissions';
import InventoryList from './inventory-components/InventoryList';
import InventoryTransactions from './inventory-components/InventoryTransactions';
import InventoryConfigs from './inventory-components/InventoryConfigs';

export default function InventoryPage() {
  const { companyId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    canViewInventoryPage,
    canViewInventoryTransactions,
    canViewInventoryConfigurations
  } = usePermissions();

  const [inventoryType, setInventoryType] = useState(() => {
    return localStorage.getItem('inventory_type_preference') || 'products';
  });

  const handleInventoryTypeChange = (type) => {
    setInventoryType(type);
    localStorage.setItem('inventory_type_preference', type);
  };
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const canViewPage    = canViewInventoryPage();
  const canViewTrans   = canViewInventoryTransactions();
  const canViewConfigs = canViewInventoryConfigurations();

  // Build visible tabs based on permissions
  const visibleTabs = [
    canViewPage    && { label: 'Inventory',      icon: <InventoryIcon />, type: 'inventory',     path: `/company/${companyId}/inventory/inventory/inventory-list`          },
    canViewTrans   && { label: 'Transactions',   icon: <TransferIcon />,  type: 'transactions',  path: `/company/${companyId}/inventory/inventory/inventory-transactions`   },
    canViewConfigs && { label: 'Configurations', icon: <SettingsIcon />,  type: 'configs',       path: `/company/${companyId}/inventory/inventory/inventory-configurations` },
  ].filter(Boolean);

  // Derive active tab from URL path
  const getActiveTabIndex = () => {
    const pathname = location.pathname;
    if (pathname.includes('inventory-transactions')) return visibleTabs.findIndex(t => t.type === 'transactions');
    if (pathname.includes('inventory-configurations')) return visibleTabs.findIndex(t => t.type === 'configs');
    return visibleTabs.findIndex(t => t.type === 'inventory');
  };
  const rawActive = getActiveTabIndex();
  const safeActiveTab = Math.max(0, Math.min(rawActive === -1 ? 0 : rawActive, visibleTabs.length - 1));

  const handleTabChange = (e, v) => {
    const tab = visibleTabs[v];
    if (tab?.path) navigate(tab.path);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // No view permissions at all — show access denied card
  if (!canViewPage && !canViewTrans && !canViewConfigs) {
    return (
      <Fade in timeout={800}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            textAlign: 'center'
          }}>
            <Card sx={{
              p: 8,
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: 'white',
              boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
              maxWidth: 600,
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                opacity: 0.8,
                transition: 'opacity 0.3s ease'
              },
              '&:hover::before': { opacity: 1 }
            }}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                mb: 6,
                display: 'inline-flex',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
              }}>
                <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              </Box>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 500, color: 'text.primary', fontSize: '2.5rem', letterSpacing: '-0.8px' }}>
                Access Restricted
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, fontWeight: 500, fontSize: '1.2rem' }}>
                Inventory Permissions Required
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 6, fontSize: '1.05rem', maxWidth: 450, mx: 'auto' }}>
                You don't currently have the necessary permissions to access the Inventory module.
                Contact your system administrator to request access or verify your role permissions.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 4, py: 1.5, fontSize: '1rem' }}
              >
                Return to Previous Page
              </Button>
            </Card>
          </Box>
        </Container>
      </Fade>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Container disableGutters maxWidth="xl" sx={{ px: 3, pb: 8 }}>
          <Paper sx={{
            borderRadius: 3,
            mt: 8,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`
            }
          }}>
            <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <Tabs
                value={safeActiveTab}
                onChange={handleTabChange}
                sx={{
                  px: 3,
                  pt: 3,
                  '& .MuiTab-root': {
                    minHeight: 72,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    px: 3,
                    py: 2,
                    mx: 1,
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 500
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    bottom: 0,
                    borderRadius: '3px 3px 0 0',
                    bgcolor: 'black',
                    zIndex: 2
                  }
                }}
              >
                {visibleTabs.map((tab, index) => (
                  <Tab
                    key={tab.type}
                    label={tab.label}
                    icon={React.cloneElement(tab.icon, { fontSize: "medium" })}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Box>

            {visibleTabs.map((tab, idx) => (
              <div key={tab.type} role="tabpanel" hidden={safeActiveTab !== idx}>
                {safeActiveTab === idx && (
                  <Box sx={{ p: 0 }}>
                    {tab.type === 'inventory' && (
                      <InventoryList
                        companyId={companyId}
                        inventoryType={inventoryType}
                        setInventoryType={handleInventoryTypeChange}
                        onShowSnackbar={showSnackbar}
                      />
                    )}
                    {tab.type === 'transactions' && (
                      <InventoryTransactions
                        companyId={companyId}
                        inventoryType={inventoryType}
                        onTypeChange={handleInventoryTypeChange}
                      />
                    )}
                    {tab.type === 'configs' && (
                      <InventoryConfigs
                        companyId={companyId}
                        inventoryType={inventoryType}
                        onTypeChange={handleInventoryTypeChange}
                      />
                    )}
                  </Box>
                )}
              </div>
            ))}
          </Paper>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ borderRadius: 2 }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Fade>
  );
}
